import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Plus, X } from "lucide-react";
import { Reveal, SectionEyebrow } from "@/components/shared/Reveal";
import { EVENT_TYPES } from "@/lib/pulseConstants";
import { handlePulseBriefClick } from "@/lib/pulseBriefCta";

/**
 * Section 04 — What We Create.
 *
 * Compact-by-default card grid: 4 cards on the first desktop row + 3 on the
 * second (via a 12-col grid, cards span 3 / 4 respectively). Each card shows
 * only its tag, title, bullets and an "Explore service" affordance. Clicking
 * opens an in-place expansion (one open at a time) that reveals the service
 * hook, capability list and a lead-form CTA.
 *
 * Accessibility: expansion is a real <button> with aria-expanded and
 * aria-controls; a matching aria-live region announces the change; Escape
 * closes the currently open card; motion respects prefers-reduced-motion via
 * Framer Motion's viewport / global reduce settings.
 */
export default function EventTypesSection() {
    const [openKey, setOpenKey] = useState(null);

    // Escape closes the currently open card.
    useEffect(() => {
        if (!openKey) return undefined;
        const onKey = (e) => { if (e.key === "Escape") setOpenKey(null); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [openKey]);

    return (
        <section className="section-pad relative hidden md:block" data-testid="home-event-types-section">
            <div className="container-editorial">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
                    <div>
                        <SectionEyebrow index="03" label="What We Create" />
                        <Reveal>
                            <h2 className="display-2 text-white text-balance">
                                Experiences designed
                                <br />
                                to <span className="text-[#FF2A2A]">earn attention.</span>
                            </h2>
                        </Reveal>
                    </div>
                    <Reveal delay={0.1}>
                        <p className="body-md max-w-md">
                            From brand launches and cultural takeovers to creator nights and product discovery&mdash;each format is built around a clear business and audience outcome.
                        </p>
                    </Reveal>
                </div>

                {/* 12-col grid: 4 x col-span-3 on row 1 + 3 x col-span-4 on row 2 */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-5">
                    {EVENT_TYPES.map((et, i) => {
                        const span = i < 4 ? "md:col-span-3" : "md:col-span-4";
                        const isOpen = openKey === et.key;
                        return (
                            <Reveal key={et.key} delay={0.04 * i} className={span}>
                                <ServiceCard
                                    et={et}
                                    isOpen={isOpen}
                                    onToggle={() => setOpenKey(isOpen ? null : et.key)}
                                />
                            </Reveal>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

function ServiceCard({ et, isOpen, onToggle }) {
    const contentId = `service-detail-${et.key}`;
    return (
        <motion.div
            whileHover={{ y: isOpen ? 0 : -2 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            data-testid={`event-type-card-${et.key}`}
            className={`card-sheen h-full rounded-2xl border bg-[#0B0C10] p-5 md:p-6 transition-colors relative overflow-hidden ${isOpen ? "border-[#FF2A2A]/60" : "border-white/10 hover:border-white/25"}`}
        >
            <div className="flex items-start justify-between mb-4">
                <span className="font-mono text-[11px] uppercase tracking-widest text-white/50">
                    {et.tag}
                </span>
                <button
                    type="button"
                    onClick={onToggle}
                    aria-expanded={isOpen}
                    aria-controls={contentId}
                    aria-label={`${isOpen ? "Close" : "Open"} ${et.title} details`}
                    data-testid={`event-type-toggle-${et.key}`}
                    className={`h-11 w-11 -mr-2 -mt-2 rounded-full border flex items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF2A2A] focus-visible:ring-offset-2 focus-visible:ring-offset-black ${isOpen ? "border-[#FF2A2A]/60 bg-[#FF2A2A] text-black" : "border-white/15 text-white/70 hover:border-[#FF2A2A]/60 hover:text-[#FF2A2A]"}`}
                >
                    {isOpen ? <X size={16} /> : <Plus size={16} />}
                </button>
            </div>
            <h3 className="font-display text-2xl md:text-3xl leading-none text-white mb-4">
                {et.title}
            </h3>
            {/* Mobile compact chip row \u2014 dot-separated capability labels
                for a materially shorter collapsed card. Hidden on md+. */}
            <div
                className="md:hidden mb-1 text-xs text-white/70 leading-relaxed"
                data-testid={`event-type-mobile-chips-${et.key}`}
            >
                {et.bullets.map((b, idx) => (
                    <span key={b} className="inline-flex items-center">
                        <span className="text-white/85">{b}</span>
                        {idx < et.bullets.length - 1 && (
                            <span aria-hidden="true" className="mx-2 text-[#FF2A2A]/70">&middot;</span>
                        )}
                    </span>
                ))}
            </div>
            {/* Desktop bullet list retained \u2014 hidden on mobile. */}
            <ul className="hidden md:block space-y-2 mb-5">
                {et.bullets.map((b) => (
                    <li key={b} className="flex items-center gap-2 text-sm text-white/80">
                        <span className="h-1 w-1 rounded-full bg-[#FF2A2A]" />
                        {b}
                    </li>
                ))}
            </ul>
            {/* "Explore Service" text link \u2014 desktop only. On mobile the
                plus/close control is the sole interaction cue per approved
                mobile spec. */}
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={isOpen}
                aria-controls={contentId}
                data-testid={`event-type-explore-${et.key}`}
                className="hidden md:inline-flex items-center gap-2 text-sm font-mono uppercase tracking-widest text-white/70 hover:text-white transition-colors min-h-11 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF2A2A] focus-visible:ring-offset-2 focus-visible:ring-offset-black rounded-sm"
            >
                {isOpen ? "Hide details" : "Explore service"}
                <ArrowUpRight size={12} className={`transition-transform ${isOpen ? "rotate-45" : ""}`} />
            </button>

            <AnimatePresence initial={false}>
                {isOpen && (
                    <motion.div
                        key="detail"
                        id={contentId}
                        role="region"
                        aria-live="polite"
                        data-testid={`event-type-detail-${et.key}`}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                        className="overflow-hidden"
                    >
                        <div className="mt-6 pt-5 border-t border-white/10 space-y-4">
                            <p className="text-sm text-white/75 leading-relaxed">
                                {et.hook}
                            </p>
                            <div>
                                <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-white/50 mb-2">
                                    Typical scope
                                </div>
                                <ul className="space-y-1.5">
                                    {et.bullets.map((b) => (
                                        <li key={`scope-${b}`} className="flex items-start gap-2 text-sm text-white/85">
                                            <span className="h-1 w-1 mt-2 rounded-full bg-[#FF2A2A] shrink-0" />
                                            {b}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <Link
                                to="/contact"
                                onClick={handlePulseBriefClick}
                                data-testid={`event-type-cta-${et.key}`}
                                className="group inline-flex items-center gap-2 mt-2 h-11 px-5 rounded-full bg-[#FF2A2A] text-black text-sm font-semibold hover:bg-[#ff4747] transition-colors"
                            >
                                Start a Pulse Brief
                                <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                            </Link>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
}
